import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMe, useRequestCode, useVerifyCode } from '../hooks/useAuth.js';
import { SpinnerButton } from '../components/SpinnerButton.js';

type Step = 'enter-email' | 'enter-code';

function unwrapWelcome(next: string): string {
  if (!next.startsWith('/welcome')) return next;
  const qIdx = next.indexOf('?');
  if (qIdx < 0) return next;
  const inner = new URLSearchParams(next.slice(qIdx + 1)).get('next');
  return inner ? unwrapWelcome(inner) : next;
}

export function LoginPage() {
  const [step, setStep] = useState<Step>('enter-email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const request = useRequestCode();
  const verify = useVerifyCode();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const nextPath = params.get('next') ?? '/';
  const errorParam = params.get('error');
  const me = useMe();

  // Already signed in (e.g. user landed on /login from a stale link, or HMR
  // left them mid-flow). Skip the form entirely and head to wherever they
  // were going.
  useEffect(() => {
    const user = me.data?.user;
    if (!user) return;
    const dest = unwrapWelcome(nextPath);
    if (!user.onboardedAt && !dest.startsWith('/welcome')) {
      navigate(`/welcome?next=${encodeURIComponent(dest)}`, { replace: true });
    } else {
      navigate(dest, { replace: true });
    }
  }, [me.data?.user, nextPath, navigate]);

  async function handleRequest(e: React.FormEvent) {
    e.preventDefault();
    await request.mutateAsync(email.trim());
    setStep('enter-code');
  }

  async function handleVerify(e: React.FormEvent) {
    e.preventDefault();
    const trimmed = code.trim();
    if (trimmed.length !== 6) return;
    const result = await verify.mutateAsync({ email, code: trimmed });
    // `nextPath` can itself be `/welcome?next=…` if the user got bounced through
    // RequireAuth at /welcome (session expired, etc.). Peel one layer so we
    // don't end up at /login?next=/welcome?next=/welcome?next=… style chains.
    const dest = unwrapWelcome(nextPath);
    if (!result.user.onboardedAt) {
      navigate(`/welcome?next=${encodeURIComponent(dest)}`, { replace: true });
    } else {
      navigate(dest, { replace: true });
    }
  }

  return (
    <div className="mx-auto max-w-md px-4 py-16">
      <div className="card p-8">
        <h1 className="text-2xl font-bold text-navy mb-2">Sign in to TowShare</h1>
        <p className="text-navy-500 text-sm mb-6">
          We'll email you a 6-digit code. No password, no fuss.
        </p>

        {errorParam && (
          <div className="mb-4 rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-700">
            {errorParam === 'google_failed' && "Google sign-in didn't work. Try again."}
            {errorParam === 'facebook_failed' && "Facebook sign-in didn't work. Try again."}
            {errorParam === 'google_unavailable' && 'Google sign-in is not configured yet.'}
            {errorParam === 'facebook_unavailable' && 'Facebook sign-in is not configured yet.'}
            {errorParam === 'missing_token' && 'That sign-in link is invalid.'}
          </div>
        )}

        {/* Social providers (Google/Facebook) are intentionally hidden.
            Backend strategies remain registered in case we re-enable them. */}

        {step === 'enter-email' && (
          <form onSubmit={handleRequest} className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-navy-700 mb-1">
                Email address
              </label>
              <input
                id="email"
                type="email"
                required
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="input"
              />
            </div>
            <SpinnerButton
              type="submit"
              className="btn-primary w-full"
              loading={request.isPending}
              disabled={!email}
            >
              {request.isPending ? 'Sending…' : 'Send code'}
            </SpinnerButton>
            {request.isError && (
              <p className="text-sm text-red-600">
                Couldn't send code. Check your email and try again.
              </p>
            )}
          </form>
        )}

        {step === 'enter-code' && (
          <form onSubmit={handleVerify} className="space-y-4">
            <p className="text-sm text-navy-600">
              Code sent to <span className="font-semibold text-navy">{email}</span>.
            </p>
            <div>
              <label htmlFor="code" className="block text-sm font-medium text-navy-700 mb-1">
                6-digit code
              </label>
              <input
                id="code"
                type="text"
                inputMode="numeric"
                pattern="[0-9]{6}"
                maxLength={6}
                required
                autoFocus
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                placeholder="123456"
                className="input text-center tracking-[0.5em] font-mono text-xl"
              />
            </div>
            <SpinnerButton
              type="submit"
              className="btn-primary w-full"
              loading={verify.isPending}
              disabled={code.length !== 6}
            >
              {verify.isPending ? 'Verifying…' : 'Verify & sign in'}
            </SpinnerButton>
            {verify.isError && (
              <p className="text-sm text-red-600">
                That code didn't work. Double-check or request a new one.
              </p>
            )}
            <button
              type="button"
              onClick={() => {
                setStep('enter-email');
                setCode('');
              }}
              className="btn-ghost w-full text-sm"
            >
              Use a different email
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

